import { navigate } from '@reach/router';
import { Formik, FormikHelpers } from 'formik';
import React, { useContext, useRef } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { userHasPermission } from '../../../authorisation/permission';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { FormErrorBox } from '../../../shared/form/Form';
import { Header1 } from '../../../shared/Headers';
import { UserContext } from '../../../users/UserContext';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { GetDataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { mapDesignFormModelToUpdateCommand, UpdateDesignCommand } from '../createUpdateDesignDto';
import { DesignForm } from '../design-form/DesignForm';
import { NextStepAfterSavingDesign } from '../design-form/DesignFormButtons';
import { DesignFormModel, designFormModelValidator } from '../design-form/designFormModel';
import { DesignResponse, mapDesignResponseToFormModel } from '../designResponse';
import { GetRequest } from '../../../shared/GetRequest';

type Props = { project: Project } & RouteComponentProps<{ projectId: string; designId: string }>;

export const EditDesign = (props: Props) => {
  const userContext = useContext(UserContext);
  const canEdit = userHasPermission({ user: userContext.getUser(), permission: 'Edit' });

  const designEndpointUrl = `projects/${props.project.projectId}/designs/${props.designId}`;

  const getDesignRequest = useApiRequest<DesignResponse>({
    endpointUrl: designEndpointUrl,
    httpMethod: 'get',
  });

  const updateDesignRequest = useApiRequest<undefined, UpdateDesignCommand>({
    endpointUrl: designEndpointUrl,
    httpMethod: 'put',
  });

  const nextStepAfterSave = useRef<NextStepAfterSavingDesign>('BackToProject');

  return (
    <GetRequest getDataRequest={getDesignRequest}>
      {({ response: designResponse }) => {
        const onSubmit = (formModel: DesignFormModel, formikHelpers: FormikHelpers<DesignFormModel>) => {
          const updateDesignCommand: UpdateDesignCommand = mapDesignFormModelToUpdateCommand(
            formModel,
            props.project.projectId,
            designResponse.pipes,
          );

          void updateDesignRequest.sendRequest({
            requestBody: updateDesignCommand,
            onSuccess: () => {
              formikHelpers.setSubmitting(false);
              if (updateDesignCommand.design.isDraft) {
                formikHelpers.setStatus('Saved as draft');
                formikHelpers.setStatus('Ready');
              } else if (nextStepAfterSave.current === 'ConfigurePdf') {
                void navigate(projectUrls.dataForDesignDrawings(props.project.projectId, designResponse.designId));
              } else {
                void navigate(projectUrls.projectDetails(props.project.projectId));
              }
            },
            onFailure: () => {
              formikHelpers.setSubmitting(false);
              formikHelpers.setStatus('Failed');
            },
          });
        };

        return (
          <>
            <BreadcrumbBar>
              <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
              <BreadcrumbSeparator />
              <BreadcrumbLink to={projectUrls.projectDetails(props.project.projectId)}>
                {props.project.projectReference}
              </BreadcrumbLink>
              <BreadcrumbSeparator />
              <div>{designResponse.fullName}</div>
              <BreadcrumbSeparator />
              <div>{canEdit ? 'Edit Design' : 'View Design'}</div>
            </BreadcrumbBar>
            <ProjectHeader project={props.project} />
            <PageContainer>
              <GetDataForQuoteOrDesignForm project={props.project}>
                {partsOptions => (
                  <>
                    <Header1>{canEdit ? 'Edit Design' : 'View Design'}</Header1>
                    {updateDesignRequest.error && <FormErrorBox error={updateDesignRequest.error} />}
                    <Formik<DesignFormModel>
                      onSubmit={onSubmit}
                      initialValues={mapDesignResponseToFormModel(
                        designResponse,
                        partsOptions,
                        props.project.buildTypeCode,
                      )}
                      validate={formModel => designFormModelValidator.validate(formModel)}
                    >
                      {formProps => (
                        <DesignForm
                          {...formProps}
                          project={props.project}
                          dataForQuoteOrDesignForm={partsOptions}
                          disabled={!canEdit}
                          setNextStepAfterSave={nextStep => {
                            nextStepAfterSave.current = nextStep;
                          }}
                        />
                      )}
                    </Formik>
                    {updateDesignRequest.error && <FormErrorBox error={updateDesignRequest.error} />}
                  </>
                )}
              </GetDataForQuoteOrDesignForm>
            </PageContainer>
          </>
        );
      }}
    </GetRequest>
  );
};
